import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CorporateAppointmentDashboardService,
  CorporateAppointmentsResponse,
  CorporateBreakdown,
  CorporateCategory,
  CorporateDashboardQuery,
  CorporateDepartment,
  CorporateFiltersResponse,
  DashboardCountsData,
  FilterOption
} from '../shared/services/corporate-appointment-dashboard.service';

type CorporateQuickFilter = 'all' | 'today' | 'ros' | 'pendingTestDrive' | 'pendingDelivery';
interface CorporateAllTotalDetail {
  label: string;
  total: number;
  today: number;
  ros: number;
}
import { CorporateDashboardState } from '../store/corporate-dashboard/corporate-dashboard.reducer';
import {
  LoadCorporateDashboardAppointments,
  LoadDashboardCounts,
  SetCorporateDashboardQuery
} from '../store/corporate-dashboard/corporate-dashboard.actions';
import {
  selectCorporateDashboardAppointments,
  selectCorporateDashboardLoading,
  selectCorporateDashboardQuery,
  selectDashboardCounts,
  selectDashboardCountsLoading,
  selectDashboardCountsLoaded
} from '../store/corporate-dashboard/corporate-dashboard.selectors';

@Component({
  selector: 'app-corporate-appointment-dashboard',
  templateUrl: './corporate-appointment-dashboard.component.html',
  styleUrls: ['./corporate-appointment-dashboard.component.scss']
})
export class CorporateAppointmentDashboardComponent implements OnInit, OnDestroy {
  categories: CorporateCategory[] = ['all', 'service', 'sales', 'parts'];
  pageSizeOptions: number[] = [20, 50, 100];

  query: CorporateDashboardQuery = {
    department: 'all',
    category: 'all',
    type: [],
    appointmentBookingSource: '',
    vehicle: '',
    dealer: '',
    todayRecords: false,
    rosReceived: false,
    pageNo: 1,
    size: 20
  };

  selectedType = '';
  selectedQuickFilter: CorporateQuickFilter = 'all';
  pageSize = 20;
  pageIndex = 0;

  isLoading = false;

  countsLoading = false;
  countsLoaded = false;

  dashboardCounts: DashboardCountsData | null = null;

  breakdown: CorporateBreakdown = {
    service: { total: 0, today: 0, ros: 0, types: [] },
    sales: { total: 0, today: 0, ros: 0, types: [] },
    parts: { total: 0, today: 0, ros: 0, types: [] }
  };

  filters: CorporateFiltersResponse = {
    vehicles: [],
    dealers: [],
    types: [] as FilterOption[],
    sources: [] as FilterOption[]
  };

  appointments: CorporateAppointmentsResponse = {
    items: [],
    totalCount: 0
  };

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly dashboardService: CorporateAppointmentDashboardService,
    private readonly corporateDashboardStore: Store<CorporateDashboardState>
  ) { }

  ngOnInit(): void {
    this.corporateDashboardStore.select(selectCorporateDashboardQuery)
      .pipe(takeUntil(this.destroy$))
      .subscribe(query => {
        this.query = {
          ...query,
          type: [...(query.type || [])]
        };
        this.selectedType = this.query.type[0] || '';
        this.selectedQuickFilter = this.resolveQuickFilter(this.query);
        this.pageSize = this.query.size || 20;
        this.pageIndex = Math.max((this.query.pageNo || 1) - 1, 0);
      });

    this.corporateDashboardStore.select(selectCorporateDashboardLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this.corporateDashboardStore.select(selectCorporateDashboardAppointments)
      .pipe(takeUntil(this.destroy$))
      .subscribe(appointments => {
        this.appointments = appointments;
      });

    this.corporateDashboardStore.select(selectDashboardCountsLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.countsLoading = loading;
      });

    this.corporateDashboardStore.select(selectDashboardCountsLoaded)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loaded => {
        this.countsLoaded = loaded;
      });

    this.corporateDashboardStore.select(selectDashboardCounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe(counts => {
        this.dashboardCounts = counts;
      });

    this.corporateDashboardStore.dispatch(new LoadDashboardCounts());

    this.loadAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDepartmentChange(department: CorporateDepartment): void {
    this.query = {
      ...this.query,
      department,
      category: 'all',
      vehicle: '',
      dealer: '',
      type: [],
      appointmentBookingSource: '',
      todayRecords: false,
      rosReceived: false,
      pageNo: 1
    };
    this.selectedType = '';
    this.selectedQuickFilter = 'all';
    this.pageIndex = 0;
    this.refreshDashboard(true);
  }

  onCategoryChange(category: CorporateCategory): void {
    this.query = {
      ...this.query,
      category,
      pageNo: 1
    };
    this.pageIndex = 0;
    this.refreshDashboard(true);
  }

  onFilterChange(): void {
    this.query.pageNo = 1;
    this.pageIndex = 0;
    this.refreshDashboard(true);
  }

  clearFilters(): void {
    this.query = {
      ...this.query,
      category: 'all',
      vehicle: '',
      dealer: '',
      type: [],
      appointmentBookingSource: '',
      todayRecords: false,
      rosReceived: false,
      pageNo: 1
    };
    this.selectedType = '';
    this.selectedQuickFilter = 'all';
    this.pageIndex = 0;
    this.refreshDashboard(true);
  }

  onTypeChange(type: string): void {
    this.selectedType = type;
    this.query = {
      ...this.query,
      type: type ? [type] : [],
      pageNo: 1
    };
    this.selectedQuickFilter = 'all';
    this.pageIndex = 0;
    this.refreshDashboard(true);
  }

  onQuickFilterChange(filter: CorporateQuickFilter): void {
    const isSameFilter = this.selectedQuickFilter === filter;
    this.selectedQuickFilter = isSameFilter ? 'all' : filter;

    let nextType: string[] = [];
    if (!isSameFilter && filter === 'pendingTestDrive') {
      nextType = ['pendingTestDrive'];
    } else if (!isSameFilter && filter === 'pendingDelivery') {
      nextType = ['pendingDelivery'];
    }

    this.query = {
      ...this.query,
      todayRecords: !isSameFilter && filter === 'today',
      rosReceived: !isSameFilter && filter === 'ros',
      type: nextType,
      pageNo: 1
    };
    this.selectedType = this.query.type[0] || '';
    this.pageIndex = 0;
    this.refreshDashboard(true);
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.query = {
      ...this.query,
      pageNo: event.pageIndex + 1,
      size: event.pageSize || this.pageSize
    };
    this.pageSize = this.query.size;
    this.refreshDashboard(false);
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 0;
    this.query = {
      ...this.query,
      size,
      pageNo: 1
    };
    this.refreshDashboard(false);
  }

  get showCategoryCardDetails(): boolean {
    return this.query.category !== 'all' && this.query.category !== 'service';
  }

  get totalCardLabel(): string {
    if (this.query.category === 'service') {
      return 'SERVICE APPOINTMENTS';
    }
    if (this.query.category === 'sales') {
      return 'SALES APPOINTMENTS';
    }
    if (this.query.category === 'parts') {
      return 'PARTS APPOINTMENTS';
    }
    return 'TOTAL BOOKED';
  }

  get totalCardSubtitle(): string {
    return this.query.category === 'all' ? 'Sales, Service, Parts' : '';
  }

  get showAllTotalDetails(): boolean {
    return this.query.category === 'all';
  }

  get totalCardAllDetails(): CorporateAllTotalDetail[] {
    if (!this.dashboardCounts) {
      return [];
    }

    return [
      {
        label: 'Service',
        total: this.dashboardCounts.service?.total ?? 0,
        today: this.dashboardCounts.todayCount?.service ?? 0,
        ros: this.dashboardCounts.roCount ?? 0
      },
      {
        label: 'Sales',
        total: this.dashboardCounts.sales?.total ?? 0,
        today: this.dashboardCounts.todayCount?.sales ?? 0,
        ros: this.breakdown.sales?.ros ?? 0
      },
      {
        label: 'Parts',
        total: this.dashboardCounts.parts ?? 0,
        today: this.dashboardCounts.todayCount?.parts ?? 0,
        ros: this.breakdown.parts?.ros ?? 0
      }
    ];
  }

  get totalCardDetails(): Array<{ label: string; value: number }> {
    if (this.query.category === 'sales') {
      return [
        { label: 'Inquiry', value: this.dashboardCounts?.sales?.inquiry ?? this.getAppointmentTypeCount('Inquiry') },
        { label: 'Test Drive', value: this.dashboardCounts?.sales?.testDrive ?? this.getAppointmentTypeCount('Test Drive') },
        { label: 'Delivery', value: this.dashboardCounts?.sales?.delivery ?? this.getAppointmentTypeCount('Delivery') },
        { label: 'Learning', value: this.dashboardCounts?.sales?.learning ?? this.getAppointmentTypeCount('Learning') },
        { label: 'Pending Test Drive', value: this.dashboardCounts?.sales?.pendingTestDrive ?? this.getAppointmentTypeCount('Pending Test Drive') },
        { label: 'Pending Delivery', value: this.dashboardCounts?.sales?.pendingDelivery ?? this.getAppointmentTypeCount('Pending Delivery') }
      ];
    }

    if (this.query.category === 'parts') {
      return [
        { label: 'Part', value: this.getAppointmentTypeCount('Part') },
        { label: 'Part + Installation', value: this.getAppointmentTypeCount('Part + Installation') }
      ];
    }

    return [];
  }

  get kpiTotalValue(): number {
    if (!this.dashboardCounts) {
      return 0;
    }
    if (this.query.category === 'service') {
      return this.dashboardCounts.service?.total ?? 0;
    }
    if (this.query.category === 'sales') {
      return this.dashboardCounts.sales?.total ?? 0;
    }
    if (this.query.category === 'parts') {
      return this.dashboardCounts.parts ?? 0;
    }
    return this.dashboardCounts.totalAppointmentCount?.total ?? 0;
  }

  get kpiTodayValue(): number {
    if (!this.dashboardCounts) {
      return 0;
    }
    if (this.query.category === 'service') {
      return this.dashboardCounts.todayCount?.service ?? 0;
    }
    if (this.query.category === 'sales') {
      return this.dashboardCounts.todayCount?.sales ?? 0;
    }
    if (this.query.category === 'parts') {
      return this.dashboardCounts.todayCount?.parts ?? 0;
    }
    return this.dashboardCounts.todayCount?.total ?? 0;
  }

  get kpiRosValue(): number {
    if (!this.dashboardCounts) {
      return 0;
    }
    if (this.query.category === 'service') {
      return this.dashboardCounts.roCount ?? 0;
    }
    if (this.query.category === 'sales' || this.query.category === 'parts') {
      return 0;
    }
    return this.dashboardCounts.roCount ?? 0;
  }

  private getAppointmentTypeCount(type: string): number {
    return this.appointments.items.filter(row => row.type === type).length;
  }

  private loadAll(): void {
    this.refreshDashboard(true);
  }

  private refreshDashboard(refreshAuxiliaryData: boolean): void {
    const nextQuery: CorporateDashboardQuery = {
      ...this.query,
      type: [...(this.query.type || [])]
    };

    this.corporateDashboardStore.dispatch(new SetCorporateDashboardQuery({
      query: nextQuery
    }));

    this.corporateDashboardStore.dispatch(new LoadCorporateDashboardAppointments({
      query: nextQuery
    }));

    if (refreshAuxiliaryData) {
      this.dashboardService.getFilters(nextQuery).subscribe(filters => {
        this.filters = filters;
      });

      this.dashboardService.getBreakdown(nextQuery).subscribe(breakdown => {
        this.breakdown = breakdown;
      });
    }
  }

  private resolveQuickFilter(query: CorporateDashboardQuery): CorporateQuickFilter {
    if (query.todayRecords) {
      return 'today';
    }

    if (query.rosReceived) {
      return 'ros';
    }

    if (query.type?.includes('pendingTestDrive')) {
      return 'pendingTestDrive';
    }

    if (query.type?.includes('pendingDelivery')) {
      return 'pendingDelivery';
    }

    return 'all';
  }
}
